import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  FaArrowLeft,
  FaCamera,
  FaStop,
  FaDownload,
  FaFileUpload,
  FaNotesMedical,
  FaPrescriptionBottleAlt,
  FaSave,
  FaStethoscope,
  FaUser,
  FaVideo,
} from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

interface RoomFile {
  id: number;
  uploaded_by: string;
  file_url: string;
  original_name: string | null;
  mime_type: string | null;
  created_at: string;
}

interface RoomAppointment {
  id: string;
  booking_id: string | null;
  fullName: string;
  phone: string;
  email: string | null;
  age: string | null;
  gender: string | null;
  department: string;
  symptoms: string | null;
  appointmentDate: string;
  appointmentTime: string;
  rawDate: string;
  rawTime: string;
  status: string;
  report_url: string | null;
  payment_proof_url: string | null;
  doctor_name: string | null;
  doctor_email: string | null;
  video_link: string | null;
  patient_join_status: string | null;
  doctor_join_status: string | null;
  patient_joined_at: string | null;
  doctor_joined_at: string | null;
  link_active_from: string | null;
  link_active_until: string | null;
  canJoinNow: number;
  isBeforeJoinWindow: number;
  isLinkExpired: number;
  consultation_notes: string | null;
  prescription_text: string | null;
  completed_at: string | null;
  missed_reason: string | null;
  files: RoomFile[];
}

const ConsultationRoom: React.FC = () => {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const backendUrl =
    window.location.hostname === "localhost"
      ? "http://localhost:8483"
      : "https://www.ayushvatika.com";

  const initialRole = searchParams.get("role") === "doctor" ? "doctor" : "patient";

  const [role, setRole] = useState<"patient" | "doctor">(initialRole);
  const [room, setRoom] = useState<RoomAppointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [notes, setNotes] = useState("");
  const [prescription, setPrescription] = useState("");
  const [uploading, setUploading] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const safeRoomId = decodeURIComponent(roomId || "");

  const meetUrl = useMemo(() => {
    const cleaned = safeRoomId.replace(/[^a-zA-Z0-9_-]/g, "-");
    return `https://meet.jit.si/ayushvatika-${cleaned}`;
  }, [safeRoomId]);

  const getFileUrl = (url?: string | null) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${backendUrl}${url}`;
  };

  const fetchRoom = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${backendUrl}/api/consultation-room/${encodeURIComponent(safeRoomId)}`);
      if (res.data?.success) {
        const data = res.data.data;
        setRoom(data);
        setNotes(data.consultation_notes || "");
        setPrescription(data.prescription_text || "");
      } else {
        alert(res.data?.message || "Room load nahi ho paaya.");
      }
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || "Consultation room load nahi ho paaya.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (safeRoomId) fetchRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeRoomId]);

  useEffect(() => {
    if (videoRef.current && localStream) {
      videoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  const startCameraPreview = async () => {
    try {
      setCameraError("");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      setLocalStream(stream);
    } catch (err: any) {
      console.error(err);
      setCameraError(
        "Camera/mic access nahi mila. Agar desktop me camera nahi hai to phone se test karein ya USB webcam use karein."
      );
    }
  };

  const stopCameraPreview = () => {
    localStream?.getTracks().forEach((track) => track.stop());
    setLocalStream(null);
  };

  const joinAsPatient = async () => {
    if (!room) return;

    try {
      setActionLoading(true);
      const res = await axios.put(`${backendUrl}/api/appointments/${room.id}/patient-join`);
      alert(res.data?.message || "Patient joined.");
      await fetchRoom();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || "Patient join nahi ho paaya.");
    } finally {
      setActionLoading(false);
    }
  };

  const joinAsDoctor = async () => {
    if (!room) return;

    try {
      setActionLoading(true);
      const res = await axios.put(
        `${backendUrl}/api/consultation-room/${encodeURIComponent(safeRoomId)}/doctor-join`
      );
      alert(res.data?.message || "Doctor joined.");
      await fetchRoom();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || "Doctor join nahi ho paaya.");
    } finally {
      setActionLoading(false);
    }
  };

  const startConsultation = async () => {
    if (!room) return;

    try {
      setActionLoading(true);
      const res = await axios.put(`${backendUrl}/api/admin/appointments/scheduled/${room.id}/start`);
      alert(res.data?.message || "Consultation start ho gaya.");
      await fetchRoom();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || "Consultation start nahi ho paaya.");
    } finally {
      setActionLoading(false);
    }
  };

  const savePrescription = async (complete = false) => {
    if (!room) return;

    try {
      setActionLoading(true);
      const res = await axios.put(
        `${backendUrl}/api/consultation-room/${encodeURIComponent(safeRoomId)}/prescription`,
        {
          notes,
          prescriptionText: prescription,
          complete,
        }
      );
      alert(res.data?.message || "Saved.");
      await fetchRoom();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || "Save nahi ho paaya.");
    } finally {
      setActionLoading(false);
    }
  };

  const uploadExtraFile = async (file?: File | null) => {
    if (!room || !file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("uploadedBy", role === "doctor" ? "DOCTOR" : "PATIENT");

      const res = await axios.post(
        `${backendUrl}/api/consultation-room/${encodeURIComponent(safeRoomId)}/files`,
        formData
      );

      alert(res.data?.message || "File upload ho gaya.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchRoom();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || "File upload nahi ho paaya.");
    } finally {
      setUploading(false);
    }
  };

  const canJoin = Number(room?.canJoinNow) === 1 && !["COMPLETED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED", "CANCELLED", "REJECTED"].includes(room?.status || "");
  const bothJoined = room?.patient_join_status === "JOINED" && room?.doctor_join_status === "JOINED";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-lg font-bold text-emerald-700">
        Consultation room loading...
      </div>
    );
  }

  if (!room) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="rounded-2xl bg-white p-8 text-center shadow">
          <h1 className="text-2xl font-black text-gray-900">Room Not Found</h1>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="sticky top-0 z-20 border-b bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="rounded-xl border border-gray-200 bg-white p-3 text-gray-700 hover:bg-gray-50"
            >
              <FaArrowLeft />
            </button>
            <div>
              <h1 className="text-xl font-black text-gray-900">Ayush Vatika Video Consultation</h1>
              <p className="text-sm font-semibold text-gray-500">
                {room.booking_id} • {room.appointmentDate} • {room.appointmentTime}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as "patient" | "doctor")}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold"
            >
              <option value="patient">Patient View</option>
              <option value="doctor">Doctor/Admin View</option>
            </select>

            <span className="rounded-full bg-blue-50 px-3 py-2 text-xs font-black text-blue-700">
              Status: {room.status}
            </span>
            <span className="rounded-full bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700">
              Patient: {room.patient_join_status || "NOT_JOINED"}
            </span>
            <span className="rounded-full bg-purple-50 px-3 py-2 text-xs font-black text-purple-700">
              Doctor: {room.doctor_join_status || "NOT_JOINED"}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-5 p-4 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <div className="overflow-hidden rounded-2xl bg-black shadow">
            <div className="aspect-video w-full">
              <iframe
                title="Ayush Vatika Video Call"
                src={meetUrl}
                allow="camera; microphone; fullscreen; display-capture"
                className="h-full w-full border-0"
              />
            </div>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-black text-gray-900">
                <FaCamera />
                Camera/Mic Preview
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={startCameraPreview}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white"
                >
                  <FaCamera />
                  Test Camera
                </button>
                <button
                  onClick={stopCameraPreview}
                  className="inline-flex items-center gap-2 rounded-xl bg-gray-700 px-4 py-2 text-sm font-bold text-white"
                >
                  <FaStop />
                  Stop
                </button>
              </div>
            </div>

            {cameraError && (
              <div className="mb-3 rounded-xl border border-red-100 bg-red-50 p-3 text-sm font-bold text-red-700">
                {cameraError}
              </div>
            )}

            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="h-56 w-full rounded-xl bg-gray-900 object-cover"
            />
            <p className="mt-2 text-xs font-semibold text-gray-500">
              Desktop me camera nahi hai to mobile browser me same room URL open karke test karein.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl bg-white p-5 shadow">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-gray-900">
              <FaUser />
              Patient Details
            </h2>
            <Info label="Patient" value={room.fullName} />
            <Info label="Mobile" value={room.phone} />
            <Info label="Email" value={room.email || "N/A"} />
            <Info label="Age / Gender" value={`${room.age || "N/A"} / ${room.gender || "N/A"}`} />
            <Info label="Department" value={room.department} />
            <Info label="Symptoms" value={room.symptoms || "No symptoms"} />

            <div className="mt-4 rounded-xl bg-blue-50 p-3 text-xs font-bold text-blue-700">
              Join window: {room.link_active_from || "N/A"} to {room.link_active_until || "N/A"}
            </div>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-gray-900">
              <FaVideo />
              Join Controls
            </h2>

            {role === "patient" ? (
              <button
                disabled={!canJoin || actionLoading || room.patient_join_status === "JOINED"}
                onClick={joinAsPatient}
                className="w-full rounded-xl bg-emerald-600 px-4 py-3 font-black text-white disabled:bg-gray-400"
              >
                Join as Patient
              </button>
            ) : (
              <div className="space-y-3">
                <button
                  disabled={!canJoin || actionLoading || room.doctor_join_status === "JOINED"}
                  onClick={joinAsDoctor}
                  className="w-full rounded-xl bg-purple-600 px-4 py-3 font-black text-white disabled:bg-gray-400"
                >
                  Join as Doctor
                </button>
                <button
                  disabled={!bothJoined || actionLoading || room.status === "IN_CONSULTATION"}
                  onClick={startConsultation}
                  className="w-full rounded-xl bg-indigo-600 px-4 py-3 font-black text-white disabled:bg-gray-400"
                >
                  Start Consultation
                </button>
              </div>
            )}

            {!canJoin && (
              <p className="mt-3 rounded-xl bg-yellow-50 p-3 text-xs font-bold text-yellow-700">
                Actual join button appointment time se 5 minute pehle active hoga aur 10 minute baad expire ho jayega.
                Admin/doctor camera test upar se kabhi bhi kar sakte hain.
              </p>
            )}
          </div>

          <div className="rounded-2xl bg-white p-5 shadow">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-gray-900">
              <FaDownload />
              Reports
            </h2>

            <div className="space-y-2">
              {room.report_url ? (
                <a
                  href={getFileUrl(room.report_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-xl bg-gray-900 px-4 py-3 text-sm font-bold text-white"
                >
                  Open Original Report
                </a>
              ) : (
                <p className="rounded-xl bg-gray-50 p-3 text-sm font-bold text-gray-500">Original report uploaded nahi hai.</p>
              )}

              {room.payment_proof_url && (
                <a
                  href={getFileUrl(room.payment_proof_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white"
                >
                  Open Payment Proof
                </a>
              )}

              {room.files?.map((file) => (
                <a
                  key={file.id}
                  href={getFileUrl(file.file_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold text-gray-700"
                >
                  {file.uploaded_by}: {file.original_name || file.file_url}
                </a>
              ))}
            </div>

            <div className="mt-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => uploadExtraFile(e.target.files?.[0])}
                className="hidden"
              />
              <button
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white disabled:bg-gray-400"
              >
                <FaFileUpload />
                {uploading ? "Uploading..." : "Upload New Report / Photo / PDF"}
              </button>
            </div>
          </div>

          {role === "doctor" && (
            <div className="rounded-2xl bg-white p-5 shadow">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-gray-900">
                <FaNotesMedical />
                Notes & Prescription
              </h2>

              <label className="mb-2 block text-xs font-black uppercase text-gray-500">Consultation Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="mb-4 w-full rounded-xl border border-gray-200 p-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-emerald-200"
                placeholder="Diagnosis, patient discussion, observations..."
              />

              <label className="mb-2 block text-xs font-black uppercase text-gray-500">Prescription / Medicine Advice</label>
              <textarea
                value={prescription}
                onChange={(e) => setPrescription(e.target.value)}
                rows={6}
                className="w-full rounded-xl border border-gray-200 p-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-emerald-200"
                placeholder="Medicine, dosage, timing, diet advice, follow-up..."
              />

              <div className="mt-4 grid grid-cols-1 gap-3">
                <button
                  disabled={actionLoading}
                  onClick={() => savePrescription(false)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-black text-white disabled:bg-gray-400"
                >
                  <FaSave />
                  Save Notes
                </button>
                <button
                  disabled={actionLoading}
                  onClick={() => savePrescription(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-black text-white disabled:bg-gray-400"
                >
                  <FaPrescriptionBottleAlt />
                  Complete & Save Prescription
                </button>
              </div>
            </div>
          )}

          {role === "patient" && (room.consultation_notes || room.prescription_text) && (
            <div className="rounded-2xl bg-white p-5 shadow">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-gray-900">
                <FaPrescriptionBottleAlt />
                Doctor Advice
              </h2>
              {room.consultation_notes && (
                <div className="mb-3 rounded-xl bg-purple-50 p-3">
                  <p className="text-xs font-black uppercase text-purple-600">Doctor Notes</p>
                  <p className="whitespace-pre-line text-sm font-semibold text-gray-700">{room.consultation_notes}</p>
                </div>
              )}
              {room.prescription_text && (
                <div className="rounded-xl bg-emerald-50 p-3">
                  <p className="text-xs font-black uppercase text-emerald-600">Prescription</p>
                  <p className="whitespace-pre-line text-sm font-semibold text-gray-700">{room.prescription_text}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Info: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="mb-3 rounded-xl bg-gray-50 p-3">
    <p className="text-xs font-black uppercase text-gray-400">{label}</p>
    <p className="break-words text-sm font-bold text-gray-800">{value}</p>
  </div>
);

export default ConsultationRoom;
